import Icon, { IconType } from 'components/Icon';
import ProfileIcon from 'components/Panel/RightPanel/ProfileIcon';
import { DropdownMenuItem } from 'components/Sidebar/dropdownStyles';
import { Tier } from 'dragonmouse-shared/interfaces';
import useSidebarDropdown from 'hooks/useSidebarDropdown';
import { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectUser } from 'redux/db/reducers';
import { setSettingsModalVisible, showDownloadsModal } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import { logout } from 'utils';
import * as S from './styles';

const AccountButton = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const navigate = useNavigate();
	const { isOpen, toggle, close, containerRef } = useSidebarDropdown();

	const { user, subscription } = useSelector(
		(r: RootReducer) => ({
			user: selectUser(r.db),
			subscription: r.db.subscription,
		}),
		shallowEqual
	);

	const currentTier = subscription?.tier ?? Tier.FREE;
	const planLabel =
		currentTier === Tier.FREE ? 'Free Plan' : currentTier === Tier.TIER_1 ? 'Pro Plan' : 'Max Plan';

	const recentExports = user?.metadata?.recentExports ?? [];
	const hasDownloads = recentExports.length > 0;
	// Only "ready and not yet downloaded" counts as unviewed — pending/failed
	// entries shouldn't flag the badge.
	const hasUnviewedDownloads = useMemo(
		() => recentExports.some((e) => (e.status ?? 'ready') === 'ready' && !e.downloadedAt),
		[recentExports]
	);

	const onMenuItemClick = (action: () => void) => {
		action();
		close();
	};

	return (
		<S.Base ref={containerRef}>
		<S.Trigger
			type="button"
			onClick={toggle}
			aria-haspopup="menu"
			aria-expanded={isOpen}
			aria-controls="account-menu"
		>
			<S.AvatarWrap>
				<ProfileIcon size={32} />
			</S.AvatarWrap>
			<S.Info>
				<S.Name>{user?.displayName || 'Account'}</S.Name>
				<S.Plan>{planLabel}</S.Plan>
			</S.Info>
			<S.Chevron>
				<Icon type={IconType.FILLED_ARROW_DOWN} width={12} height={12} color={theme.gray[600]} />
			</S.Chevron>
		</S.Trigger>
		{isOpen && (
			<S.Menu id="account-menu" role="menu">
					<DropdownMenuItem
						type="button"
						onClick={() => onMenuItemClick(() => dispatch(setSettingsModalVisible(true)))}
					>
						<Icon type={IconType.SETTINGS} width={14} height={14} color={theme.gray[1000]} />
						Settings
					</DropdownMenuItem>
					<DropdownMenuItem
						type="button"
						onClick={() => onMenuItemClick(() => navigate(routes.profile()))}
					>
						<Icon type={IconType.USER} width={14} height={14} color={theme.gray[1000]} />
						Profile
					</DropdownMenuItem>
					{hasDownloads && (
						<DropdownMenuItem
							type="button"
							onClick={() => onMenuItemClick(() => dispatch(showDownloadsModal()))}
						>
							<Icon
								type={IconType.DOWNLOAD}
								width={14}
								height={14}
								color={theme.gray[1000]}
							/>
							Downloads
							{hasUnviewedDownloads && <S.MenuItemDot />}
						</DropdownMenuItem>
					)}
					<DropdownMenuItem type="button" onClick={() => onMenuItemClick(() => logout())}>
						<Icon type={IconType.LOG_OUT} width={14} height={14} color={theme.gray[1000]} />
						Log out
					</DropdownMenuItem>
				</S.Menu>
			)}
		</S.Base>
	);
};

export default AccountButton;
